import { useUnsavedValue } from '../lib/unsaved.tsx';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { ArrowRight, CalendarDays, Ellipsis, Mail, Paperclip, X } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type DragEvent, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import {
  checkAttachment,
  chatterFilterLabels,
  chatterFilters,
  COLLAB_TEXTS,
  emailOriginText,
  emailStatusText,
  emailTemplateText,
  mentionedIds,
  NOTE_MAX_LENGTH,
  newNoteSchema,
  newTaskSchema,
  NOTE_TASK_TEXTS,
  type ChatterFilter,
  type TaskPriority,
  buildNoteTaskDraft,
} from '@till-erp/collab/values';
import { documentAttachmentText } from '@till-erp/mail/values';
import { workAreaLabels } from '@till-erp/platform/user-values';
import {
  apiFetch,
  apiFetchBlob,
  ApiProblem,
  newIdempotencyKey,
  type Assignee,
  type ChatterAttachment,
  type ChatterEmail,
  type ChatterItem,
  type ChatterPage,
  type ChatterTask,
  type FollowState,
} from '../lib/api.ts';
import { formatDate, formatDateTime, formatFileSize } from '../lib/format.ts';
import { chatterDay, chatterDayLabel } from '../lib/chatter-day.ts';
import { useToday } from '../lib/business-time.ts';
import { useSession } from '../lib/session.tsx';
import { mailPollStep, WAITING_EMAIL, type MailWaitSince } from '../lib/chatter-poll.ts';
import { cn } from '../lib/utils.ts';
import { MailComposer } from './mail-composer.tsx';
import { dayMonth, NewTaskSheet, PriorityChooser, type NoteTaskSource } from './task-detail.tsx';
import { useUndo } from './undo-toast.tsx';
import { Checkbox } from './ui/checkbox.tsx';
import { Popover } from './ui/popover.tsx';
import { Button } from './ui/button.tsx';
import { CardTitle } from './ui/card-title.tsx';
import { DateField } from './ui/date-field.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Segment } from './ui/segment.tsx';
import { useCardHeadTabs } from './ui/tab-card.tsx';
import { Select } from './ui/select.tsx';
import { Textarea } from './ui/textarea.tsx';

type FieldErrors = Record<string, string>;

/** Ruhiger Textknopf der Werkzeugleiste im Verlauf (Wiedervorlage · E-Mail · Datei anhängen, Ticket 1.312); bei Touch 44 px Trefferfläche. */
const TOOL_TEXT_BUTTON = 'type-meta min-h-6 cursor-pointer text-link hover:underline disabled:cursor-default disabled:opacity-50 touch-hit';

function problemText(err: unknown): string {
  return err instanceof ApiProblem ? err.detail : 'Etwas ist schiefgelaufen. Bitte noch einmal versuchen.';
}

/** Erste Meldung je Feld aus einer Zod-Prüfung (dieselben Schemas wie der Server). */
function fieldErrors(issues: readonly { path: readonly PropertyKey[]; message: string }[]): FieldErrors {
  const errors: FieldErrors = {};
  for (const issue of issues) errors[String(issue.path[0] ?? '_')] ??= issue.message;
  return errors;
}

function FieldError({ id, text }: { id: string; text: string | undefined }) {
  if (!text) return null;
  return (
    <p id={id} className="type-meta mt-space-2 text-clarify">
      {text}
    </p>
  );
}

function chatterKey(entityType: string, entityId: string, filter?: ChatterFilter) {
  return filter ? (['chatter', entityType, entityId, filter] as const) : (['chatter', entityType, entityId] as const);
}

/** Inhalt einer Datei als Base64 (ohne `data:`-Vorsatz). */
function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result ?? '');
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('read'));
    reader.readAsDataURL(file);
  });
}

/**
 * Datei anhängen: wählen (Knopf) oder in den Chatter ziehen. Prüft Größe und Namen vor dem Senden mit
 * derselben Regel wie der Server; die Höchstgröße kommt aus der Einstellung.
 */
function useAttachmentUpload(entityType: string, entityId: string, maxBytes: number | null, onSaved: () => void) {
  const { getToken } = useSession();
  const [error, setError] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: async (file: File) =>
      apiFetch<ChatterItem>(getToken, `/api/v1/chatter/${entityType}/${entityId}/attachments`, {
        method: 'POST',
        body: { fileName: file.name, contentType: file.type, contentBase64: await readBase64(file) },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiProblem ? (Object.values(err.errors)[0] ?? err.detail) : problemText(err)),
  });
  function send(file: File | undefined) {
    setError(null);
    if (!file) return;
    if (maxBytes === null) {
      setError(COLLAB_TEXTS.chatterLoading);
      return;
    }
    const errors = checkAttachment({ fileName: file.name, sizeBytes: file.size }, maxBytes);
    const first = errors.file ?? errors.fileName;
    if (first) {
      setError(first);
      return;
    }
    upload.mutate(file);
  }
  return { send, error, pending: upload.isPending };
}

/**
 * Story Selbstabholung: „Nachweis hochladen …“ außerhalb des Chatters (Panel der Liste „Abholbereit“) – dieselbe Ablage
 * wie der Chatter (`POST /api/v1/chatter/{typ}/{id}/attachments`, Person und Zeit am Eintrag), Höchstgröße aus der
 * ersten Chatter-Seite. Nach dem Speichern lädt der Chatter des Datensatzes neu.
 */
export function useRecordAttachmentUpload(entityType: string, entityId: string, onSaved: () => void) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const first = useQuery({
    queryKey: [...chatterKey(entityType, entityId), 'upload-limit'],
    queryFn: () => apiFetch<ChatterPage>(getToken, `/api/v1/chatter/${entityType}/${entityId}?limit=1`),
  });
  return useAttachmentUpload(entityType, entityId, first.data?.attachmentMaxBytes ?? null, () => {
    void queryClient.invalidateQueries({ queryKey: chatterKey(entityType, entityId) });
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    onSaved();
  });
}

/** Datei öffnen: PDF, Bilder und Text in einem neuen Tab; alles andere nur herunterladen (nie als Seite öffnen). */
async function openAttachment(getToken: () => Promise<string | null>, path: string, file: ChatterAttachment): Promise<void> {
  // Fenster sofort öffnen (sonst blockt der Browser es nach dem Laden als Popup).
  const win = file.safeToOpen ? window.open('', '_blank') : null;
  try {
    const blob = await apiFetchBlob(getToken, path);
    const typed = new Blob([blob], { type: file.safeToOpen ? file.contentType : 'application/octet-stream' });
    const url = URL.createObjectURL(typed);
    if (win) {
      win.location.href = url;
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      a.rel = 'noopener';
      document.body.append(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    win?.close();
    throw err;
  }
}

/** Text nach einem „@“ vor der Schreibmarke (Anfang oder nach Leerraum), höchstens 40 Zeichen, ohne Zeilenwechsel. */
function mentionAt(text: string, caret: number): { start: number; caret: number; query: string } | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf('@');
  if (start < 0 || (start > 0 && !/\s/.test(before[start - 1]!))) return null;
  const query = before.slice(start + 1);
  if (query.length > 40 || /[\n@]/.test(query)) return null;
  return { start, caret, query };
}

/** Personen zur @-Auswahl: Name beginnt mit dem Text oder ein Namensteil beginnt damit. */
function mentionMatches(people: readonly Assignee[], query: string): Assignee[] {
  const q = query.toLocaleLowerCase('de-DE');
  return people.filter((p) => {
    const name = p.displayName.toLocaleLowerCase('de-DE');
    return name.startsWith(q) || name.split(/\s+/).some((part) => part.startsWith(q));
  });
}

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** `2027-02-24` → „Mi 24.02.“ (Chip „Fällig“ am Häkchen). */
function weekdayDayMonth(date: string): string {
  return `${WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()]} ${dayMonth(date)}`;
}

/**
 * Häkchen „als Aufgabe“ unter der Notiz (Story 23.20, FR-63, Entwurf A): nennt jede erwähnte Person; angehakt je Name ein
 * Chip mit ×, dazu „Fällig“ und Priorität aus der festen Deutung des Notiztexts (`buildNoteTaskDraft`). Je Person entsteht
 * beim Speichern genau eine Aufgabe.
 */
function NoteAsTask({
  on,
  people,
  deselected,
  dueDate,
  priority,
  today,
  onToggle,
  onRemove,
  onDueDate,
  onPriority,
}: {
  on: boolean;
  people: readonly Assignee[];
  deselected: ReadonlySet<string>;
  dueDate: string;
  priority: TaskPriority;
  today: string;
  onToggle: (on: boolean) => void;
  onRemove: (id: string) => void;
  onDueDate: (date: string) => void;
  onPriority: (p: TaskPriority) => void;
}) {
  const labelId = useId();
  const dueId = useId();
  // Fällig-Chip: Tippen öffnet das vorhandene Datumsfeld mit Kalender an seiner Stelle (DESIGN U23).
  const [editDue, setEditDue] = useState(false);
  const shown = people.filter((p) => !deselected.has(p.id));
  return (
    <div className="mt-space-2 flex flex-wrap items-center gap-x-space-2 gap-y-space-1" data-testid="als-aufgabe">
      <Checkbox checked={on} onCheckedChange={onToggle} aria-labelledby={labelId} className="pointer-coarse:size-6" data-testid="als-aufgabe-haken" />
      <span id={labelId} className="cursor-pointer text-row text-ink-2" onClick={() => onToggle(!on)}>
        {NOTE_TASK_TEXTS.checkbox} für
      </span>
      {on ? (
        shown.map((p) => (
          <span key={p.id} className="inline-flex h-6 items-center gap-space-1 rounded-full bg-brand-tile pr-space-1 pl-space-2 text-caption text-brand-tile-fg" data-testid="als-aufgabe-person">
            {p.displayName}
            <button
              type="button"
              aria-label={`${p.displayName} abwählen`}
              title={`${p.displayName} abwählen`}
              onClick={() => onRemove(p.id)}
              className="grid size-5 cursor-pointer place-items-center rounded-full hover:bg-card/60 touch-hit"
            >
              <X size={12} strokeWidth={2} aria-hidden />
            </button>
          </span>
        ))
      ) : (
        <span className="text-row text-ink-2" data-testid="als-aufgabe-namen">
          {people.map((p) => p.displayName).join(', ')}
        </span>
      )}
      {on ? (
        <>
          {editDue ? (
            <span className="inline-flex items-center gap-space-1" data-testid="als-aufgabe-faellig-feld">
              <label htmlFor={dueId} className="text-caption text-ink-2">
                Fällig
              </label>
              <DateField id={dueId} value={dueDate} onChange={(v) => v && onDueDate(v)} today={today} min={today} className="w-36" />
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setEditDue(true)}
              aria-label={`Fällig ${weekdayDayMonth(dueDate)} ändern`}
              className="inline-flex min-h-9 cursor-pointer items-center rounded-field hover:bg-tile pointer-coarse:min-h-11"
            >
              <span className="inline-flex h-7 items-center gap-space-1 rounded-full bg-tile px-space-3 text-caption text-ink-2" data-testid="als-aufgabe-faellig">
                <CalendarDays size={12} strokeWidth={2} aria-hidden />
                <span className="sr-only">Fällig </span>
                {weekdayDayMonth(dueDate)}
              </span>
            </button>
          )}
          <span data-testid="als-aufgabe-prioritaet">
            <PriorityChooser value={priority} onChange={onPriority} busy={false} />
          </span>
        </>
      ) : null}
    </div>
  );
}

/**
 * Notiz schreiben mit @Erwähnung (FR-61): „@“ öffnet eine Liste aktiver Personen mit Leserecht am Datensatz, gefiltert nach dem Text
 * danach; ↑/↓, Enter oder Tab bzw. Klick fügen „@Anzeigename “ ein, Esc schließt die Liste. Gesendet werden
 * die gewählten Personen, deren „@Name“ beim Speichern noch im Text steht.
 */
function NoteForm({
  entityType,
  entityId,
  today,
  onSaved,
  tools,
}: {
  entityType: string;
  entityId: string;
  /** Fachliches Heute (Server); ohne kein Häkchen „als Aufgabe“. */
  today: string | null;
  /** Gespeichert; `taskIds` = mit „als Aufgabe“ angelegte Aufgaben (für Rückgängig). */
  onSaved: (taskIds: string[]) => void;
  /** Ruhige Werkzeugleiste links in der Zeile des Knopfs (Wiedervorlage · E-Mail · Datei anhängen); `null` = keine. */
  tools?: ReactNode;
}) {
  const { getToken, me } = useSession();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Assignee[]>([]);
  // Story 23.20 (FR-63): Häkchen „als Aufgabe“ – je erwähnter Person eine Aufgabe; einzelne Personen per × abwählbar.
  const [asTask, setAsTask] = useState(false);
  const [deselected, setDeselected] = useState<ReadonlySet<string>>(new Set());
  // Chips Fällig und Priorität: `null` = Wert aus der Deutung des Notiztexts; geändert = Wahl der Person.
  const [dueChoice, setDueChoice] = useState<string | null>(null);
  const [priorityChoice, setPriorityChoice] = useState<TaskPriority | null>(null);
  const [picker, setPicker] = useState<{ start: number; caret: number; query: string } | null>(null);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState<number | null>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  // Ungespeicherte Notiz (EXPERIENCE §4.3): „Speichern“ in der Leiste speichert sie.
  useUnsavedValue({ body, picked }, () => fieldRef.current?.form?.requestSubmit());
  const caretAfterRender = useRef<number | null>(null);
  const ids = { field: useId(), err: useId(), list: useId() };
  // Nur Personen, die diesen Datensatz sehen dürfen (Bug 28, Rudolf F2).
  const people = useQuery({
    queryKey: ['assignees', entityType, entityId],
    queryFn: () =>
      apiFetch<{ items: Assignee[] }>(
        getToken,
        `/api/v1/tasks/assignees?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
      ),
    staleTime: 60_000,
  });
  const matches = picker && picker.start !== dismissed ? mentionMatches(people.data?.items ?? [], picker.query).slice(0, 8) : [];
  const listOpen = matches.length > 0;
  const activeIndex = Math.min(active, Math.max(matches.length - 1, 0));
  // Erwähnte Personen, deren „@Name“ noch im Text steht (ohne die schreibende Person – sie bekommt keine Aufgabe über das Häkchen).
  const mentionedNow = picked.filter((p) => p.id !== me.id && mentionedIds(body, [p]).length > 0);
  const chosen = mentionedNow.filter((p) => !deselected.has(p.id));
  const taskOn = asTask && today !== null && chosen.length > 0;
  // Fällig und Priorität aus der festen Deutung der Schnell-Erfassung (keine zweite Deutung, keine KI).
  const draft = today && mentionedNow.length > 0 ? buildNoteTaskDraft({ body, today, mentioned: mentionedNow, self: me.id }) : null;
  const taskDue = dueChoice ?? draft?.dueDate ?? today ?? '';
  const taskPriority = priorityChoice ?? draft?.priority ?? 'normal';
  const save = useMutation({
    mutationFn: (input: { body: string; mentions: string[]; assigneeIds: string[]; dueDate: string; priority: TaskPriority }) =>
      apiFetch<ChatterItem>(getToken, `/api/v1/chatter/${entityType}/${entityId}/notes`, {
        method: 'POST',
        body: {
          body: input.body,
          ...(input.mentions.length > 0 ? { mentions: input.mentions } : {}),
          ...(input.assigneeIds.length > 0 ? { asTask: { assigneeIds: input.assigneeIds, dueDate: input.dueDate, priority: input.priority } } : {}),
        },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (saved) => {
      setBody('');
      setPicked([]);
      setPicker(null);
      setAsTask(false);
      setDeselected(new Set());
      setDueChoice(null);
      setPriorityChoice(null);
      onSaved((saved.noteTasks ?? []).map((t) => t.id));
    },
    onError: (err) => {
      const fieldText = err instanceof ApiProblem ? (err.errors.body ?? err.errors.mentions ?? err.errors.asTask) : undefined;
      setError(fieldText ?? problemText(err));
      fieldRef.current?.focus();
    },
  });

  /** Stand der @-Auswahl nach Eingabe oder Bewegen der Schreibmarke. */
  function track(field: HTMLTextAreaElement) {
    const found = mentionAt(field.value, field.selectionStart ?? field.value.length);
    // Gewählte Erwähnung („@Name “) ist fertig: nicht erneut als Suche lesen, solange der Name unverändert dasteht.
    const next = found && picked.some((p) => found.query.startsWith(p.displayName)) ? null : found;
    if (next?.query !== picker?.query || next?.start !== picker?.start) setActive(0);
    if (next === null || next.start !== dismissed) setDismissed(null);
    setPicker(next);
  }

  function choose(person: Assignee) {
    if (!picker) return;
    const insert = `@${person.displayName} `;
    const text = body.slice(0, picker.start) + insert + body.slice(picker.caret);
    const caret = picker.start + insert.length;
    setBody(text);
    setPicked((prev) => (prev.some((p) => p.id === person.id) ? prev : [...prev, person]));
    setPicker(null);
    // Schreibmarke hinter den Namen setzen, sobald der neue Text im Feld steht (vor der nächsten Eingabe; ein
    // späteres requestAnimationFrame konnte schnell getippte Zeichen hinter die Marke schieben).
    caretAfterRender.current = caret;
  }

  useLayoutEffect(() => {
    const caret = caretAfterRender.current;
    if (caret === null) return;
    caretAfterRender.current = null;
    fieldRef.current?.focus();
    fieldRef.current?.setSelectionRange(caret, caret);
  }, [body]);

  // Das Notizfeld wächst mit dem Text (mindestens drei Zeilen), damit „@Name (Test)“ nie abgeschnitten wird; erst an der
  // Grenze (`max-h-60`) erscheint ein Scrollbalken (Ticket 1.312).
  // Im verborgenen Teil „Verlauf“ (`hidden`) misst das Feld 0 – dann nicht messen (es bleibt bei `rows={3}`) und beim
  // Fokus nachmessen, sobald es sichtbar ist.
  const fitHeight = useCallback(() => {
    const field = fieldRef.current;
    if (!field || field.offsetParent === null) return;
    field.style.height = 'auto';
    field.style.height = `${field.scrollHeight + field.offsetHeight - field.clientHeight}px`;
    field.style.overflowY = field.scrollHeight > field.clientHeight + 1 ? 'auto' : 'hidden';
  }, []);
  useLayoutEffect(fitHeight, [body, fitHeight]);

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!listOpen || e.ctrlKey || e.altKey || e.metaKey) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((activeIndex + step + matches.length) % matches.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      choose(matches[activeIndex]!);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setDismissed(picker!.start);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = newNoteSchema.safeParse({ body });
    if (!parsed.success) {
      setError(fieldErrors(parsed.error.issues).body ?? COLLAB_TEXTS.noteEmpty);
      fieldRef.current?.focus();
      return;
    }
    save.mutate({ body: parsed.data.body, mentions: mentionedIds(parsed.data.body, picked), assigneeIds: taskOn ? chosen.map((p) => p.id) : [], dueDate: taskDue, priority: taskPriority });
  }

  return (
    <form noValidate onSubmit={submit} aria-label="Notiz schreiben">
      <label htmlFor={ids.field} className="sr-only">
        Notiz
      </label>
      <div className="relative">
        <Textarea
          id={ids.field}
          ref={fieldRef}
          value={body}
          maxLength={NOTE_MAX_LENGTH}
          rows={3}
          placeholder="Notiz schreiben … (@ erwähnt jemanden)"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={listOpen}
          aria-controls={listOpen ? ids.list : undefined}
          aria-activedescendant={listOpen ? `${ids.list}-${activeIndex}` : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? ids.err : undefined}
          onChange={(e) => {
            setBody(e.target.value);
            track(e.target);
          }}
          onSelect={(e) => track(e.currentTarget)}
          onKeyDown={onKeyDown}
          onBlur={() => setPicker(null)}
          onFocus={fitHeight}
          invalid={!!error}
          // Ticket 1.312: ohne Zieh-Ecke, wächst mit dem Text bis zur Grenze.
          className="max-h-60 min-h-0 resize-none"
        />
        {listOpen ? (
          <ul
            id={ids.list}
            role="listbox"
            aria-label="Person erwähnen"
            className="absolute inset-x-0 top-full z-20 mt-space-1 flex list-none flex-col rounded-tile bg-card p-space-1 shadow-float"
          >
            {matches.map((person, i) => (
              <li
                key={person.id}
                id={`${ids.list}-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                // Vor dem Verlassen des Feldes wählen (onBlur schließt die Liste).
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(person);
                }}
                className={cn(
                  'flex min-h-9 cursor-pointer items-center rounded-field px-space-3 text-row text-ink',
                  i === activeIndex && 'bg-tile',
                )}
              >
                {person.displayName}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <FieldError id={ids.err} text={error ?? undefined} />
      {draft && mentionedNow.length > 0 ? (
        <NoteAsTask
          on={asTask && chosen.length > 0}
          people={mentionedNow}
          deselected={deselected}
          dueDate={taskDue}
          priority={taskPriority}
          today={today!}
          onDueDate={setDueChoice}
          onPriority={setPriorityChoice}
          onToggle={(next) => {
            setAsTask(next);
            setDeselected(new Set());
            setDueChoice(null);
            setPriorityChoice(null);
          }}
          onRemove={(id) => {
            const next = new Set(deselected).add(id);
            // Letzte Person abgewählt: Häkchen aus, alle wieder da.
            if (mentionedNow.every((p) => next.has(p.id))) {
              setAsTask(false);
              setDeselected(new Set());
            } else setDeselected(next);
          }}
        />
      ) : null}
      {/* Ticket 1.312 (Foto Rudolf 31.10.2026): eine ruhige Zeile – links Wiedervorlage · E-Mail · Datei anhängen, rechts
          „Notiz hinzufügen“ klein und sekundär, gesperrt ohne Text. */}
      <div className="mt-space-2 flex items-center justify-between gap-x-space-3">
        {/* Werkzeuge brechen bei schmaler Leiste links in sich um; der Knopf bleibt rechts in derselben Zeile. */}
        <div className="min-w-0 flex-1">{tools}</div>
        <Button type="submit" variant="secondary" size="sm" disabled={save.isPending || body.trim() === ''} className="ml-auto flex-none">
          Notiz hinzufügen
        </Button>
      </div>
    </form>
  );
}

/** Wiedervorlage am Datensatz; auch aus Listen (Story 4.22: „Kunde anrufen“ je schlafendem Kunden) mit vorbelegtem Titel. */
export function TaskForm({
  entityType,
  entityId,
  today,
  onSaved,
  onCancel,
  initialTitle = '',
  initialAssigneeId = '',
  heading,
}: {
  entityType: string;
  entityId: string;
  today: string;
  onSaved: () => void;
  onCancel: () => void;
  initialTitle?: string;
  /** Vorbelegung „Zuständig“ (schnelle Handlung in Listen, z. B. „Schlafende Kunden“: der Angemeldete). */
  initialAssigneeId?: string;
  /** Kopfzeile über den Feldern (schnelle Handlung außerhalb des Datensatzes: wofür die Wiedervorlage ist). */
  heading?: string;
}) {
  const { getToken } = useSession();
  const [assigneeId, setAssigneeId] = useState(initialAssigneeId);
  const [dueDate, setDueDate] = useState(today);
  // Getippter, unbrauchbarer Text im Datumsfeld sperrt das Speichern (DateField `onInvalidChange`).
  const [typedDueError, setTypedDueError] = useState<string | null>(null);
  const [title, setTitle] = useState(initialTitle);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const firstField = useRef<HTMLSelectElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  // Angefangene Wiedervorlage (EXPERIENCE §4.3).
  useUnsavedValue({ assigneeId, dueDate, title }, () => titleRef.current?.form?.requestSubmit());

  /** Fehler am Feld zeigen und den Fokus zum ersten Fehler setzen (EXPERIENCE §4.3). */
  function showErrors(next: FieldErrors) {
    setErrors(next);
    const first = next.assigneeId ? firstField.current : next.dueDate ? document.getElementById(ids.due) : next.title ? titleRef.current : null;
    if (first) requestAnimationFrame(() => first.focus());
    else setFormError(Object.values(next)[0] ?? 'Bitte die Angaben prüfen.');
  }
  useEffect(() => firstField.current?.focus(), []);
  const ids = { assignee: useId(), assigneeErr: useId(), due: useId(), dueErr: useId(), title: useId(), titleErr: useId() };
  const assignees = useQuery({
    queryKey: ['assignees'],
    queryFn: () => apiFetch<{ items: Assignee[] }>(getToken, '/api/v1/tasks/assignees'),
  });
  const save = useMutation({
    mutationFn: (input: { title: string; assigneeId: string; dueDate: string }) =>
      apiFetch(getToken, `/api/v1/chatter/${entityType}/${entityId}/tasks`, { method: 'POST', body: input, idempotencyKey: newIdempotencyKey() }),
    onSuccess: onSaved,
    onError: (err) => {
      if (err instanceof ApiProblem && Object.keys(err.errors).length > 0) showErrors(err.errors);
      else setFormError(problemText(err));
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setFormError(null);
    if (typedDueError) {
      showErrors({ dueDate: typedDueError });
      return;
    }
    const parsed = newTaskSchema.safeParse({ title, assigneeId, dueDate });
    if (!parsed.success) {
      showErrors(fieldErrors(parsed.error.issues));
      return;
    }
    save.mutate(parsed.data);
  }

  return (
    <form noValidate onSubmit={submit} aria-label="Wiedervorlage anlegen" className="mt-space-4 flex flex-col gap-space-4 rounded-tile bg-tile p-space-4">
      {heading ? (
        <p className="text-row font-semibold text-ink" data-testid="wiedervorlage-kopf">
          {heading}
        </p>
      ) : null}
      <div>
        <Label htmlFor={ids.assignee} required>
          Zuständig
        </Label>
        <Select
          id={ids.assignee}
          ref={firstField}
          value={assigneeId}
          invalid={!!errors.assigneeId}
          aria-describedby={errors.assigneeId ? ids.assigneeErr : undefined}
          onChange={(e) => setAssigneeId(e.target.value)}
        >
          <option value="">Bitte wählen</option>
          {assignees.data?.items.map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
            </option>
          ))}
        </Select>
        <FieldError id={ids.assigneeErr} text={errors.assigneeId} />
      </div>
      <div>
        <Label htmlFor={ids.due} required>
          Fällig am
        </Label>
        <DateField
          id={ids.due}
          className="w-full"
          value={dueDate}
          today={today}
          invalid={!!errors.dueDate}
          aria-describedby={errors.dueDate ? ids.dueErr : undefined}
          onChange={setDueDate}
          onInvalidChange={setTypedDueError}
        />
        <FieldError id={ids.dueErr} text={errors.dueDate} />
      </div>
      <div>
        <Label htmlFor={ids.title} required>
          Titel
        </Label>
        <Input
          id={ids.title}
          ref={titleRef}
          value={title}
          invalid={!!errors.title}
          aria-describedby={errors.title ? ids.titleErr : undefined}
          placeholder="Was ist zu tun?"
          onChange={(e) => setTitle(e.target.value)}
        />
        <FieldError id={ids.titleErr} text={errors.title} />
      </div>
      {formError ? (
        <p role="alert" className="type-meta rounded-tile bg-clarify-bg px-space-4 py-space-3 text-clarify">
          {formError}
        </p>
      ) : null}
      <div className="flex flex-wrap-reverse items-center justify-end gap-x-space-4 gap-y-space-2">
        <button type="button" onClick={onCancel} className="type-meta cursor-pointer text-ink-2 hover:underline">
          Abbrechen
        </button>
        <Button type="submit" disabled={save.isPending}>
          Wiedervorlage anlegen
        </Button>
      </div>
    </form>
  );
}

function AttachmentEntry({ file, entityType, entityId }: { file: ChatterAttachment; entityType: string; entityId: string }) {
  const { getToken } = useSession();
  const [error, setError] = useState<string | null>(null);
  const path = `/api/v1/chatter/${entityType}/${entityId}/attachments/${file.id}`;
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setError(null);
          openAttachment(getToken, path, file).catch((err: unknown) => setError(problemText(err)));
        }}
        className="flex min-h-6 cursor-pointer items-center gap-space-2 text-left text-row text-ink hover:underline [overflow-wrap:anywhere]"
        aria-label={`${file.fileName} ${file.safeToOpen ? 'öffnen' : 'herunterladen'}`}
      >
        <Paperclip aria-hidden size={16} strokeWidth={1.5} className="flex-none text-ink-2" />
        {file.fileName}
      </button>
      <span className="type-meta block text-ink-2">
        {formatFileSize(file.sizeBytes)} · {file.safeToOpen ? 'öffnen' : 'herunterladen'}
      </span>
      {error ? <span className="type-meta block text-clarify">{error}</span> : null}
    </>
  );
}

const clock = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' });

/**
 * E-Mail im Chatter: Empfänger und Betreff, Anhänge (Belege mit Nummer, Fassung und Prüfsumme, Story 3.10), Vorlage mit Version und Sprache (Schnappschuss, Story 2.7),
 * darunter „automatisch“ bzw. „von Hand: Name“ und der Zustand (R-30); der Text zum Aufklappen.
 */
function EmailEntry({ email, authorName }: { email: ChatterEmail; authorName: string }) {
  const time = email.statusAt ? clock.format(new Date(email.statusAt)) : null;
  const failed = email.status === 'failed' || email.status === 'bounced';
  const templateText = emailTemplateText(email.template, email.language);
  return (
    <>
      <span className="flex items-start gap-space-2 text-row text-ink [overflow-wrap:anywhere]">
        <Mail aria-hidden size={16} strokeWidth={1.5} className="mt-space-1 flex-none text-ink-2" />
        <span>
          E-Mail an {email.recipient}: {email.subject}
        </span>
      </span>
      {email.attachments.length > 0 ? (
        <ul aria-label="Anhänge der E-Mail" className="mt-space-1 flex list-none flex-col p-0">
          {email.attachments.map((a, i) => (
            <li key={i} className="type-meta flex items-start gap-space-2 text-ink-2 [overflow-wrap:anywhere]">
              <Paperclip aria-hidden size={14} strokeWidth={1.5} className="mt-space-1 flex-none" />
              {a.document ? (
                // Beleg aus dem Archiv (Story 3.10): Nummer, Fassung und Prüfsumme des archivierten PDF.
                <span>
                  {documentAttachmentText(a.document)} · {formatFileSize(a.sizeBytes)}
                  <span className="block">Prüfsumme (SHA-256) {a.sha256}</span>
                </span>
              ) : (
                <span>
                  {a.fileName} · {formatFileSize(a.sizeBytes)}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {templateText ? <span className="type-meta block text-ink-2">{templateText}</span> : null}
      <span className={cn('type-meta block', failed ? 'text-clarify' : 'text-ink-2')}>
        {emailOriginText(email.origin, authorName)} · {emailStatusText(email.status, { time, detail: email.detail })}
      </span>
      <details className="mt-space-1">
        <summary className="type-meta min-h-6 cursor-pointer text-ink-2 hover:underline">Text der E-Mail</summary>
        <p className="mt-space-1 text-row whitespace-pre-wrap text-ink [overflow-wrap:anywhere]">{email.body}</p>
      </details>
    </>
  );
}

/** Satz eines Protokolleintrags; mit Verweis (Story 9.23) wird dessen Teil („Auftrag 100005“) zum Link. */
function ChangeText({ change }: { change: NonNullable<ChatterItem['change']> }) {
  const at = change.link ? change.text.indexOf(change.link.label) : -1;
  if (!change.link || at < 0) return <span className="block text-row text-ink-2 [overflow-wrap:anywhere]">{change.text}</span>;
  const { label, href } = change.link;
  return (
    <span className="block text-row text-ink-2 [overflow-wrap:anywhere]">
      {change.text.slice(0, at)}
      <Link to={href} data-testid="chatter-verweis" className="text-link hover:underline">
        {label}
      </Link>
      {change.text.slice(at + label.length)}
    </span>
  );
}

function EntryText({ item, entityType, entityId }: { item: ChatterItem; entityType: string; entityId: string }) {
  if (item.kind === 'email' && item.email) {
    return <EmailEntry email={item.email} authorName={item.authorName} />;
  }
  if (item.kind === 'attachment' && item.attachment) {
    return <AttachmentEntry file={item.attachment} entityType={entityType} entityId={entityId} />;
  }
  if (item.kind === 'change' && item.change) {
    return <ChangeText change={item.change} />;
  }
  if (item.kind === 'task_created' && item.task) {
    return (
      <>
        <span className="block text-row text-ink">
          {item.task.group === 'approvals' ? 'Freigabe' : 'Wiedervorlage'}: {item.task.title}
        </span>
        <span className="type-meta block text-ink-2">
          {item.task.assigneeName
            ? `Zuständig ${item.task.assigneeName}`
            : item.task.area
              ? `Für Bereich ${workAreaLabels[item.task.area]}`
              : item.task.area === null
                ? 'Für Admins'
                : 'Für den Bereich'}{' '}
          · fällig {formatDate(item.task.dueDate)}
        </span>
      </>
    );
  }
  return <span className="block text-row whitespace-pre-wrap text-ink [overflow-wrap:anywhere]">{item.body}</span>;
}

/**
 * „Folgen: …“ und Knopf Folgen / Nicht mehr folgen (FR-61): Wer folgt, sieht Änderungen am Datensatz in der
 * Glocke (ohne eigene Änderungen).
 */
function FollowLine({
  entityType,
  entityId,
  state,
  onChanged,
}: {
  entityType: string;
  entityId: string;
  state: FollowState | null;
  onChanged: (state: FollowState) => void;
}) {
  const { getToken } = useSession();
  const [error, setError] = useState<string | null>(null);
  const toggle = useMutation({
    mutationFn: (following: boolean) =>
      apiFetch<FollowState>(getToken, `/api/v1/chatter/${entityType}/${entityId}/follow`, {
        method: following ? 'DELETE' : 'POST',
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (next) => {
      setError(null);
      onChanged(next);
    },
    onError: (err) => setError(problemText(err)),
  });
  if (!state) return null;
  const names = state.followers.map((f) => f.displayName).join(', ');
  return (
    <div className="mt-space-3">
      <div className="flex flex-wrap items-center justify-between gap-x-space-3 gap-y-space-1">
        <p className="type-meta text-ink-3" data-testid="folgen">
          Folgen: {names || 'noch niemand'}
        </p>
        <button
          type="button"
          disabled={toggle.isPending}
          onClick={() => toggle.mutate(state.following)}
          className="type-meta min-h-6 cursor-pointer text-ink-2 hover:underline disabled:cursor-default disabled:opacity-50 touch-hit"
        >
          {state.following ? 'Nicht mehr folgen' : 'Folgen'}
        </button>
      </div>
      {error ? <p className="type-meta mt-space-1 text-clarify">{error}</p> : null}
    </div>
  );
}

/** Punkt der Zeitleiste je Art (Ticket 1.272): Menschen-Einträge blau, E-Mails Info, Systemeinträge (Änderungen) leiser. */
const CHATTER_DOT: Readonly<Record<ChatterItem['kind'], string>> = {
  note: 'bg-blue-300',
  task_created: 'bg-blue-300',
  attachment: 'bg-blue-300',
  email: 'bg-info',
  change: 'bg-chip-line',
};

/** Menü „…“ an einer Notiz (Story 23.20, Entwurf A): „Als Aufgabe …“. Am Rechner bei Hover/Fokus, bei Touch immer sichtbar. */
function NoteMenu({ onAsTask }: { onAsTask: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn('relative flex-none', !open && 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100')}>
      <Popover
        open={open}
        onOpenChange={setOpen}
        label="Weitere Aktionen zur Notiz"
        align="end"
        className="w-52 border border-line p-space-2"
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-label="Weitere Aktionen zur Notiz"
            className="-my-space-1 grid size-7 cursor-pointer place-items-center rounded-full text-ink-2 hover:bg-tile pointer-coarse:size-11"
            data-testid="notiz-menue"
          >
            <Ellipsis aria-hidden size={16} strokeWidth={1.5} />
          </button>
        )}
      >
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onAsTask();
          }}
          className="flex min-h-9 w-full cursor-pointer items-center rounded-tile px-space-2 text-left text-row hover:bg-tile pointer-coarse:min-h-11"
        >
          {NOTE_TASK_TEXTS.menuItem}
        </button>
      </Popover>
    </div>
  );
}

/** Leise Pill „→ Aufgabe“ je nicht gelöschter Aufgabe aus der Notiz (Link auf die Aufgabe); mehrere: mit Namen. */
function NoteTaskPills({ tasks }: { tasks: readonly ChatterTask[] }) {
  return (
    <span className="mt-space-1 flex flex-wrap gap-space-1">
      {tasks.map((t) => (
        <Link
          key={t.id}
          to="/aufgaben/$taskId"
          params={{ taskId: t.id }}
          title={`${t.title} · ${t.assigneeName}`}
          className="inline-flex h-6 items-center gap-space-1 rounded-full bg-tile px-space-2 text-caption text-ink-2 hover:bg-chip-line/40 hover:text-ink touch-hit"
          data-testid="notiz-aufgabe"
        >
          <ArrowRight size={12} strokeWidth={2} aria-hidden />
          {NOTE_TASK_TEXTS.pill}
          {tasks.length > 1 ? <span className="text-ink-3">· {t.assigneeName}</span> : null}
        </Link>
      ))}
    </span>
  );
}

/** Anker eines Eintrags (Link aus der Glocke: `…#eintrag-<id>`). */
function entryAnchor(id: string): string {
  return `eintrag-${id}`;
}

/**
 * Verlauf (Chatter) am Datensatz (EXPERIENCE §4.1, DESIGN „Verlauf (Chatter)“): Inhalt der Seitenleiste `RecordSidebar`
 * – nur dort eingesetzt (Ticket 1.312), in einer ruhigen Fassung. Oben „Notiz schreiben …“ mit der Zeile Wiedervorlage ·
 * E-Mail (Story 2.7) · Datei anhängen (auch per Ziehen) und „Notiz hinzufügen“, darunter der Filter Alles / Notizen /
 * E-Mails / Änderungen und die Einträge, neueste zuerst, je mit Zeit und Person. Sichtbar heißt die Karte „Verlauf“, für
 * Hilfsmittel „Verlauf (Chatter)“.
 */
export function ChatterColumn({ entityType, entityId }: { entityType: string; entityId: string }) {
  // Reiter des Rahmens („Kennzahlen · Verlauf“) im Kopf statt des Titels ([RUDOLF 19.10.2026]).
  const headTabs = useCardHeadTabs('Verlauf');
  const { getToken, me } = useSession();
  const queryClient = useQueryClient();
  const [taskOpen, setTaskOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  // Story 23.20: „Als Aufgabe …“ an einer Notiz öffnet das Blatt „Neue Aufgabe“ vorbelegt; danach 10 s Rückgängig (Papierkorb).
  const [fromNote, setFromNote] = useState<NoteTaskSource | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const undo = useUndo((err) => setNotice(problemText(err)));
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ChatterFilter>('all');
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const headingId = useId();
  const fileErrorId = useId();
  const waitingSince = useRef<MailWaitSince | null>(null);
  const chatter = useInfiniteQuery({
    queryKey: chatterKey(entityType, entityId, filter),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      apiFetch<ChatterPage>(
        getToken,
        `/api/v1/chatter/${entityType}/${entityId}?limit=30&filter=${filter}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    getNextPageParam: (last) => last.nextCursor,
    // Wartende E-Mails: Zustand nachladen, bis sie gesendet (oder endgültig) sind. Die erste Minute im kurzen Takt (der
    // Worker liefert meist binnen Sekunden), danach im langen (Tagesgrenze, Gegenstelle nicht erreichbar: Stunden).
    refetchInterval: (query) => {
      const hasWaiting = !!query.state.data?.pages.some((p) => p.items.some((i) => i.email && WAITING_EMAIL.has(i.email.status)));
      // Startzeit gilt je Datensatz und Filter; bei Wechsel beginnt die schnelle Minute neu.
      const step = mailPollStep(hasWaiting, `${entityType}:${entityId}:${filter}`, waitingSince.current, Date.now());
      waitingSince.current = step.since;
      return step.interval;
    },
  });
  const items = chatter.data?.pages.flatMap((p) => p.items) ?? [];
  const first = chatter.data?.pages[0];
  const today = first?.today ?? null;
  // Tageszeilen „Heute“/„Gestern“ nach dem fachlichen Heute (Testuhr), nie nach der Uhr des Browsers.
  const businessToday = useToday();
  const maxBytes = first?.attachmentMaxBytes ?? null;
  const followState: FollowState | null = first ? { following: first.following, followers: first.followers } : null;

  // Link aus der Glocke: zum Eintrag scrollen und ihn kurz hervorheben.
  const hash = useLocation({ select: (l) => l.hash.replace(/^#/, '') });
  const target = hash.startsWith('eintrag-') ? hash : null;
  // Neuer Anker: Eintrag kann im aktuellen Filter fehlen, daher auf „Alles“ wechseln (Muster „Zustand aus Eingabe“).
  const [anchorSeen, setAnchorSeen] = useState(target);
  if (target !== anchorSeen) {
    setAnchorSeen(target);
    if (target) setFilter('all');
  }
  const present = target !== null && items.some((i) => entryAnchor(i.id) === target);
  const pageCount = chatter.data?.pages.length ?? 0;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = chatter;
  // Je Anker nur einmal suchen; danach darf die Person den Filter wieder frei wählen.
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!target || handled.current === target) return;
    if (present) {
      handled.current = target;
      document.getElementById(target)?.scrollIntoView({ block: 'center' });
      return;
    }
    if (pageCount === 0 || isFetchingNextPage) return;
    // Nicht auf den geladenen Seiten: weitere Seiten nachladen (höchstens 10), sonst aufgeben.
    if (pageCount < 10 && hasNextPage) void fetchNextPage();
    else handled.current = target;
  }, [target, present, pageCount, hasNextPage, isFetchingNextPage, fetchNextPage]);

  /** Neu laden; `text` = Hinweis unter dem Feld (`null` = keiner, z. B. wenn der Toast mit Rückgängig schon meldet). */
  const refresh = (text: string | null) => {
    setNotice(text);
    void queryClient.invalidateQueries({ queryKey: chatterKey(entityType, entityId) });
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    void queryClient.invalidateQueries({ queryKey: ['activity'] });
    // Neue Datei: im Composer „Dateien des Datensatzes“ sofort wählbar.
    void queryClient.invalidateQueries({ queryKey: ['mail-compose', entityType, entityId] });
  };
  const upload = useAttachmentUpload(entityType, entityId, maxBytes, () => refresh('Datei angehängt.'));
  /** 10 s Rückgängig für Aufgaben aus einer Notiz (Papierkorb); eine Aufgabe mit Link „ansehen“. */
  // Nur der Toast meldet (Sichtung HS PR #1565: am Handy sonst doppelt) – kein Hinweis zusätzlich unter dem Feld.
  const offerTaskUndo = (taskIds: readonly string[], lead: string = NOTE_TASK_TEXTS.created) => {
    const single = taskIds.length === 1 ? taskIds[0]! : null;
    undo.offer({
      text: single ? (
        <>
          {lead} ·{' '}
          <Link to="/aufgaben/$taskId" params={{ taskId: single }} className="underline underline-offset-2">
            ansehen
          </Link>
        </>
      ) : (
        lead
      ),
      undo: async () => {
        await apiFetch(getToken, '/api/v1/tasks/delete', { method: 'POST', body: { ids: [...taskIds] }, idempotencyKey: newIdempotencyKey() });
        refresh(taskIds.length === 1 ? 'Aufgabe in den Papierkorb gelegt.' : 'Aufgaben in den Papierkorb gelegt.');
      },
    });
  };
  /**
   * Menü „Als Aufgabe …“ (Story 23.20): Hat die Notiz schon eine (nicht gelöschte) Aufgabe, gleich diese öffnen – die
   * der Person aus der Deutung, sonst die erste (auch wenn im Blatt jemand anderes gewählt wurde); der zweite Klick legt
   * nichts an. Sonst das Blatt „Neue Aufgabe“ vorbelegt.
   */
  const asTaskFrom = (item: ChatterItem, today: string) => {
    const draft = buildNoteTaskDraft({ body: item.body ?? '', today, mentioned: item.mentions ?? [], self: me.id });
    const tasks = item.noteTasks ?? [];
    const existing = tasks.find((t) => t.assigneeId === draft.assigneeId) ?? tasks[0];
    if (existing) {
      void navigate({ to: '/aufgaben/$taskId', params: { taskId: existing.id } });
      return;
    }
    setFromNote({
      entryId: item.id,
      authorName: item.authorName,
      at: item.at,
      body: item.body ?? '',
      entityType,
      entityId,
      mentioned: item.mentions ?? [],
      ...(first?.record ? { record: first.record } : {}),
    });
  };

  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes('Files');
  const dropProps = {
    onDragOver: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: (e: DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragging(false);
      setNotice(null);
      upload.send(e.dataTransfer.files[0]);
    },
  };
  // Story 5.121 (EK10 „dateifeld“): sichtbare Ablagefläche um „Datei anhängen“ und das Dateifeld; legt selbst ab (gleicher
  // Weg wie das Dateifeld) und hält das Ereignis an, damit die Karte nicht ein zweites Mal hochlädt.
  const zoneProps = {
    onDragOver: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragging(true);
    },
    onDrop: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      setNotice(null);
      upload.send(e.dataTransfer.files[0]);
    },
  };

  // Verstecktes Datei-Feld hinter „Datei anhängen“ (nur eingehängt, solange die Knöpfe stehen – wie bisher).
  const fileField = (
    <input
      ref={fileInput}
      type="file"
      className="sr-only"
      tabIndex={-1}
      aria-label="Datei wählen"
      onChange={(e) => {
        setNotice(null);
        upload.send(e.target.files?.[0]);
        e.target.value = '';
      }}
    />
  );

  return (
    <section
      aria-labelledby={headingId}
      className={cn('card-box relative', dragging && 'outline-2 outline-offset-2 outline-ink outline-dashed')}
      {...dropProps}
    >
      {headTabs}
      {headTabs ? (
        <h2 id={headingId} className="sr-only">
          Verlauf (Chatter)
        </h2>
      ) : (
        <CardTitle inline id={headingId}>
          Verlauf<span className="sr-only"> (Chatter)</span>
        </CardTitle>
      )}
      <div className="mt-space-3">
        <NoteForm
          entityType={entityType}
          entityId={entityId}
          today={today}
          tools={
            !taskOpen && !mailOpen ? (
              <div className="flex flex-wrap items-center gap-x-space-3 gap-y-space-1" data-testid="verlauf-werkzeuge">
                <button type="button" className={TOOL_TEXT_BUTTON} onClick={() => setTaskOpen(true)} disabled={!today}>
                  Wiedervorlage
                </button>
                <button
                  type="button"
                  className={TOOL_TEXT_BUTTON}
                  onClick={() => {
                    setNotice(null);
                    setMailOpen(true);
                  }}
                >
                  E-Mail
                </button>
                {/* Story 5.121 (EK10 „dateifeld“): Knopf und Dateifeld als eigene, sichtbare Ablagefläche. */}
                <span data-dropzone="Datei anhängen" className="relative inline-flex items-center" {...zoneProps}>
                  <button
                    type="button"
                    className={TOOL_TEXT_BUTTON}
                    onClick={() => fileInput.current?.click()}
                    disabled={maxBytes === null || upload.pending}
                    aria-describedby={upload.error ? fileErrorId : undefined}
                  >
                    {upload.pending ? 'Wird angehängt …' : 'Datei anhängen'}
                  </button>
                  {fileField}
                </span>
              </div>
            ) : null
          }
          onSaved={(taskIds) => {
            const n = taskIds.length;
            // Story 23.20: Häkchen-Weg mit 10 s Rückgängig (alle angelegten Aufgaben in den Papierkorb); Meldung nur im Toast.
            refresh(n > 0 ? null : 'Notiz gespeichert.');
            if (n > 0) offerTaskUndo(taskIds, `Notiz gespeichert · ${n === 1 ? '1 Aufgabe' : `${n} Aufgaben`} angelegt`);
          }}
        />
        {mailOpen ? (
          <MailComposer
            entityType={entityType}
            entityId={entityId}
            onCancel={() => setMailOpen(false)}
            onSent={(to) => {
              setMailOpen(false);
              setFilter('all');
              refresh(`E-Mail an ${to} eingestellt.`);
            }}
          />
        ) : taskOpen && today ? (
          <TaskForm
            entityType={entityType}
            entityId={entityId}
            today={today}
            onCancel={() => setTaskOpen(false)}
            onSaved={() => {
              setTaskOpen(false);
              refresh('Wiedervorlage angelegt.');
            }}
          />
        ) : null}
        {upload.error ? (
          <p id={fileErrorId} role="alert" className="type-meta mt-space-2 text-clarify">
            {upload.error}
          </p>
        ) : null}
        <p aria-live="polite" className="type-meta mt-space-2 text-ink-3 empty:mt-0">
          {dragging ? 'Datei hier ablegen, um sie anzuhängen.' : notice}
        </p>
      </div>
      <FollowLine
        entityType={entityType}
        entityId={entityId}
        state={followState}
        onChanged={(next) => {
          // Neuen Stand in alle geladenen Filter übernehmen, ohne den Chatter neu zu laden.
          queryClient.setQueriesData<InfiniteData<ChatterPage>>({ queryKey: chatterKey(entityType, entityId) }, (old) =>
            old ? { ...old, pages: old.pages.map((p, i) => (i === 0 ? { ...p, ...next } : p)) } : old,
          );
        }}
      />
      <div className="mt-space-2">
        <Segment
          label="Filter"
          options={chatterFilters.map((f) => ({ value: f, label: chatterFilterLabels[f] }))}
          value={filter}
          onChange={setFilter}
          dense
        />
      </div>
      {chatter.isPending ? <p className="type-meta mt-space-4 text-ink-3">Lädt …</p> : null}
      {chatter.error ? <p className="type-meta mt-space-4 text-clarify">{problemText(chatter.error)}</p> : null}
      {chatter.data && items.length === 0 ? (
        <p className="mt-space-4 text-row text-ink-3">{filter === 'emails' ? COLLAB_TEXTS.emailsEmpty : 'Noch keine Einträge.'}</p>
      ) : null}
      {items.length > 0 ? (
        <ol aria-label="Einträge im Chatter" className="mt-space-2 flex list-none flex-col p-0">
          {items.map((item, index) => {
            // Ticket 1.272 (S3, Vorbild Shopify): nach Tag gruppiert – der erste Eintrag eines Tages trägt die Tageszeile
            // („Heute“, „Gestern“, Datum; Europe/Berlin). Kein eigenes Listenelement, damit Einträge gezählt bleiben.
            const day = chatterDay(item.at);
            const firstOfDay = index === 0 || chatterDay(items[index - 1]!.at) !== day;
            const system = item.kind === 'change';
            return (
              <li
                key={item.id}
                id={entryAnchor(item.id)}
                data-chatter-kind={item.kind}
                className={cn('flex min-w-0 scroll-mt-24 flex-col', target === entryAnchor(item.id) && '-mx-space-2 rounded-tile bg-tile px-space-2')}
              >
                {firstOfDay ? (
                  <p className={cn('m-0 text-caption font-medium text-ink-2', index > 0 ? 'pt-space-4' : 'pt-space-2')} data-chatter-day={day}>
                    {chatterDayLabel(day, today ?? businessToday)}
                  </p>
                ) : null}
                <div className="group flex min-w-0 gap-space-3 py-space-2">
                  <span aria-hidden className={cn('mt-space-2 size-space-2 flex-none rounded-full', CHATTER_DOT[item.kind])} data-timeline-dot="" />
                  <div className={cn('flex min-w-0 flex-1 flex-col', system && 'text-ink-2')}>
                    <span className="flex min-w-0 items-start gap-space-2">
                      <span className="type-meta block min-w-0 flex-1 text-ink-3">
                        {formatDateTime(item.at)} · {item.authorName}
                        {item.fromRecord ? <span data-testid="chatter-herkunft"> · Aus {item.fromRecord}</span> : null}
                      </span>
                      {/* Notizen aus einem zusammengeführten Datensatz (Story 4.11, „Aus …“): kein „Als Aufgabe“ – die Aufgabe hinge sonst am alten Datensatz. */}
                      {item.kind === 'note' && item.body !== null && !item.fromRecord && today ? <NoteMenu onAsTask={() => asTaskFrom(item, today)} /> : null}
                    </span>
                    <EntryText item={item} entityType={entityType} entityId={entityId} />
                    {item.kind === 'note' && item.noteTasks && item.noteTasks.length > 0 ? <NoteTaskPills tasks={item.noteTasks} /> : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      ) : null}
      {chatter.hasNextPage ? (
        <Button variant="secondary" className="mt-space-3" disabled={chatter.isFetchingNextPage} onClick={() => void chatter.fetchNextPage()}>
          Ältere laden
        </Button>
      ) : null}
      {fromNote && today ? (
        <NewTaskSheet
          key={fromNote.entryId}
          today={today}
          fromNote={fromNote}
          onClose={() => setFromNote(null)}
          onCreated={(created) => {
            setFromNote(null);
            if (created.existing) {
              // Gab es schon (z. B. parallel angelegt): die vorhandene Aufgabe öffnen.
              undo.dismiss();
              refresh(NOTE_TASK_TEXTS.existing);
              void navigate({ to: '/aufgaben/$taskId', params: { taskId: created.id } });
              return;
            }
            refresh(null);
            offerTaskUndo([created.id]);
          }}
        />
      ) : null}
      {undo.element}
    </section>
  );
}
